import { ConnectionState } from "fluid-framework";
import { Duration } from "luxon";
import { useCallback, useEffect, useRef } from "react";

import type { FluidContainer } from "../fluid.client.js";

const WAIT_BEFORE_RECONNECT_TRY = Duration.fromObject({
  seconds: 10,
}).toMillis();
const RECONNECT_DELAY_BETWEEN_ATTEMPTS = Duration.fromObject({
  seconds: 5,
}).toMillis();
const RECONNECT_BACKOFF_FACTOR = Duration.fromObject({ seconds: 1 }).toMillis();
const RECONNECT_BACKOFF_BASE = 2;
const RECONNECT_BACKOFF_MAX_DELAY = Duration.fromObject({
  seconds: 10,
}).toMillis();
const RECONNECT_MAX_ATTEMPTS = 5;

interface UseReconnectManagerProps {
  container: FluidContainer | null;
  onConnected: () => void;
  onDisconnected: () => void;
}

interface UseReconnectManagerReturn {
  retryConnection: (immediate?: boolean) => void;
}

export function useReconnectManager({
  container,
  onConnected,
  onDisconnected,
}: UseReconnectManagerProps): UseReconnectManagerReturn {
  const timeoutBeforeDisconnection = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isReconnecting = useRef<boolean>(false);
  const lastAttemptToReconnect = useRef<number>(0);
  const reconnectionAttemptCount = useRef<number>(0);

  const cleanup = useCallback(() => {
    if (timeoutBeforeDisconnection.current) {
      clearTimeout(timeoutBeforeDisconnection.current);
      timeoutBeforeDisconnection.current = null;
    }

    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    reconnectionAttemptCount.current = 0;
    lastAttemptToReconnect.current = 0;
    isReconnecting.current = false;
  }, []);

  // ? Ensure cleanup is called when the component unmounts - to prevent memory leaks from timeout/timeout instances
  useEffect(
    () => () => {
      cleanup();
    },
    [cleanup]
  );

  const shouldAttemptToReconnect = useCallback(() => {
    const now = Date.now();

    // Prevent multiple reconnection attempts
    if (isReconnecting.current) {
      return false;
    }

    // Give some time between attempts
    if (
      now - lastAttemptToReconnect.current <
      RECONNECT_DELAY_BETWEEN_ATTEMPTS
    ) {
      return false;
    }

    if (reconnectionAttemptCount.current >= RECONNECT_MAX_ATTEMPTS) {
      return false;
    }

    return true;
  }, []);

  const attemptReconnection = useCallback(() => {
    if (!container || container.disposed) {
      return;
    }

    // Already connected
    if (container.connectionState === ConnectionState.Connected) {
      cleanup();
      onConnected();
      return;
    }

    if (!shouldAttemptToReconnect()) {
      return;
    }

    const backoffDelay = Math.min(
      RECONNECT_BACKOFF_FACTOR *
        RECONNECT_BACKOFF_BASE ** reconnectionAttemptCount.current,
      RECONNECT_BACKOFF_MAX_DELAY
    );
    isReconnecting.current = true;

    // Clear previous reconnect attempts
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    reconnectTimeout.current = setTimeout(async () => {
      if (!container || container.disposed) {
        return;
      }

      if (container.connectionState === ConnectionState.Connected) {
        cleanup();
        onConnected();
        return;
      }

      lastAttemptToReconnect.current = Date.now();
      reconnectionAttemptCount.current += 1;

      try {
        await container.connect();
      } catch {
        if (reconnectionAttemptCount.current >= RECONNECT_MAX_ATTEMPTS) {
          console.error("Maximum reconnection attempts reached");

          cleanup();
          onDisconnected();
        }
      } finally {
        isReconnecting.current = false;
      }
    }, backoffDelay);

    return () => {
      cleanup();
    };
  }, [
    onConnected,
    shouldAttemptToReconnect,
    container,
    onDisconnected,
    cleanup,
  ]);

  const retryConnection = useCallback(
    (immediate = false): void => {
      cleanup();

      if (immediate) {
        attemptReconnection();
        return;
      }

      timeoutBeforeDisconnection.current = setTimeout(() => {
        if (!container || container.disposed) {
          return;
        }

        if (container.connectionState !== ConnectionState.Connected) {
          attemptReconnection();
        }
      }, WAIT_BEFORE_RECONNECT_TRY);
    },
    [attemptReconnection, container, cleanup]
  );

  return {
    retryConnection,
  };
}
